// CoordiListPage.tsx
import React, {useEffect, useState} from "react";
import {useNavigate, useParams} from 'react-router-dom';
import {getAppliedCoordinators, getJobPostingDetail} from '../../api/jobApi';
import {CoordinatorData} from "../../interface/CoordinatorData";
import {EmptyState} from "../../components/common/EmptyStateProps";
import {toggleFavorite} from "../../api/coordiApi";
import {setComma} from "../../utils/format";
import defaultMaleProfileImg from "../../assets/images/man_default_img.png";
import defaultFemaleProfileImg from "../../assets/images/woman_default_img.png";

const CoordiListPage: React.FC = () => {
    const navigate = useNavigate();
    const {jobPostingCode} = useParams<{ jobPostingCode: string }>();
    const [coordiList, setCoordiList] = useState<CoordinatorData[]>([]);
    const [matchedCoordinatorCode, setMatchedCoordinatorCode] = useState<string | null>(null);
    const [jobStatus, setJobStatus] = useState<string>('');

    useEffect(() => {
        fetchCoordinators();
        fetchJobStatus();
    }, [jobPostingCode]);

    const fetchJobStatus = async () => {
        try {
            const response = await getJobPostingDetail(jobPostingCode || '');
            const jobData = response.data?.data || response.data;
            if (jobData?.status) {
                setJobStatus(jobData.status);
            }
            // sessionStorage 대신 API 응답에서 직접 읽도록 변경
            const coordinatorCodeFromApi = jobData?.coordinatorCode ?? null;
            setMatchedCoordinatorCode(coordinatorCodeFromApi);
        } catch (error) {
            console.error('Failed to fetch job status:', error);
        }
    };

    const isRecruiting = jobStatus === 'RECRUITING';

    const fetchCoordinators = async () => {
        try {
            const response = await getAppliedCoordinators(jobPostingCode || '');
            setCoordiList(response.data);
        } catch (error) {
            console.error('Failed to fetch coordinators:', error);
        }
    };

    const handleToggleFavorite = async (coordinatorCode: string) => {
        try {
            const response = await toggleFavorite(coordinatorCode);
            const newFavoriteState = response.data;

            // 리스트에서 해당 코디네이터의 favorite 상태 업데이트
            setCoordiList(prev =>
                prev.map(co =>
                    co.coordinatorCode === coordinatorCode
                        ? {...co, favorite: newFavoriteState}
                        : co
                )
            );
        } catch (error) {
            console.error('Failed to toggle favorite:', error);
        }
    };

    const handleDetailClick = (coordinatorCode: string) => {
        navigate(`/announcement/${jobPostingCode}/coordi/${coordinatorCode}`);
    };

    const handlePaymentClick = (coordinatorCode: string) => {
        if (!isRecruiting) {
            alert('모집중인 공고만 코디를 선택할 수 있습니다.');
            return;
        }
        navigate(`/payment?jobPostingCode=${jobPostingCode}&coordinatorCode=${coordinatorCode}`);
    };

    if (!coordiList || coordiList.length === 0) {
        return <EmptyState message="지원한 코디가 없습니다."/>;
    }

    // 매칭된 코디네이터가 있으면 해당 코디만 필터링, 없으면 전체 리스트
    const filteredCoordiList = matchedCoordinatorCode && matchedCoordinatorCode !== "null"
        ? coordiList.filter(co => co.coordinatorCode === matchedCoordinatorCode)
        : coordiList;
    return (
        <main>
            <section>
                <div className="inner-box">
                    {filteredCoordiList.map(co => (
                        <div key={co.coordinatorCode} className="round-box">
                            <div className="round-body">
                                <div className="coordi-profile-box">
                                    <div className="coordi-info">
                                        <p className="name">
                                            {co.unmaskedName || co.coordinatorName}
                                            <span className="career">
    {co && (
        (Number(co.careerYear) === 0 && Number(co.careerMonth) === 0)
            ? '미경력'
            : `경력 ${[
                Number(co.careerYear) > 0 ? `${co.careerYear}년` : '',
                Number(co.careerMonth) > 0 ? `${co.careerMonth}월` : ''
            ].filter(Boolean).join(' ')}`
    )}
</span>
                                        </p>
                                        <p className="pay">
                                            <span className="badge">{co.workType === 'HOURLY' ? '시급' : '일급'}</span>
                                            {setComma(co.amount)}원
                                        </p>
                                    </div>
                                    <span className="like-check">
                                        <input
                                            type="checkbox"
                                            id={`favorites${co.coordinatorCode}`}
                                            className="blind"
                                            checked={co.favorite || false}
                                            onChange={() => handleToggleFavorite(co.coordinatorCode)}
                                        />
                                        <label htmlFor={`favorites${co.coordinatorCode}`}></label>
                                    </span>

                                    <div className="profile-img">
                                        <div className="thumbnail-wrap">
                                            <div className="thumbnail">
                                                <div className="centered">
                                                    <img
                                                        src={co.imagePath && co.imagePath !== 'default' ? co.imagePath : (co.gender === '여성' ? defaultFemaleProfileImg : defaultMaleProfileImg)}
                                                        className="portrait"
                                                        alt="프로필"
                                                        onError={(e) => {
                                                            (e.target as HTMLImageElement).src = co.gender === '여성' ? defaultFemaleProfileImg : defaultMaleProfileImg;
                                                        }}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="coordi-cate">
                                    <div className="ico-info">
                                        <p className="tit">나이</p>
                                        <p className="cont">{co.age}</p>
                                    </div>
                                    <div className="ico-info line">
                                        <p className="tit">성별</p>
                                        <p className="cont">{co.gender}</p>
                                    </div>
                                    <div className="ico-info line">
                                        <p className="tit">국적</p>
                                        <p className="cont">{co.nationalityName}</p>
                                    </div>
                                </div>

                                <ul className="career-tit">
                                    <li>
                                        <span className="tit-cont complete">
                                            완료 <strong>{co.completed || 0}건</strong>
                                        </span>
                                    </li>
                                    <li>
                                        <span className="tit-cont award">
                                            자격증 <strong>{co.certs || 0}개</strong>
                                        </span>
                                    </li>
                                    <li>
                                        <span className="tit-cont service">
                                            서비스 후기 <strong>{co.reviewCount || 0}건</strong>
                                        </span>
                                    </li>
                                </ul>

                                <div className="info-row foot">
                                    <button
                                        className="foot-line-btn"
                                        onClick={() => handleDetailClick(co.coordinatorCode)}
                                    >
                                        자세히 보기
                                    </button>
                                    {/* 매칭된 코디네이터가 없고 모집중일 때만 코디 선택 버튼 표시 */}
                                    {!matchedCoordinatorCode && isRecruiting && (
                                        <button
                                            className="foot-submit-btn"
                                            onClick={() => handlePaymentClick(co.coordinatorCode)}
                                        >
                                            코디 선택
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </section>
        </main>
    );
};

export default CoordiListPage;